import type { RouteRecordRaw } from 'vue-router';
import middleware from '@/router/middleware';
import authentication from '@/router/middleware/authentication';
import { gamification, gamificationConfig, gamificationRanking } from '@/router/middleware/gamification';
import unauthentication from '@/router/middleware/unauthentication';
import { adminAuth } from '@/router/middleware/admin-routes';
import { useAuthStore } from '@/store/auth.store';
import debugRoutes from '@/router/middleware/debug-routes';

const routes: RouteRecordRaw[] = [
  {
    path: '/sign-in',
    name: 'SignIn',
    component: () => import('@/pages/auth-sign-in/AuthSignIn.vue'),
    beforeEnter: middleware([unauthentication]),
    meta: { noLayout: true },
  },
  {
    path: '/sign-in-site',
    name: 'SignInSite',
    component: () => import('@/pages/auth-sign-in-site/AuthSignInSite.vue'),
    beforeEnter: middleware([unauthentication]),
    meta: { noLayout: true },
  },
  {
    path: '/token',
    name: 'token',
    async beforeEnter(to, from, next) {
      const authStore = useAuthStore();
      const route = await authStore.retrieveToken(to.query.authorization_code as string);
      next(route as Parameters<typeof next>[0]);
    },
    meta: { noLayout: true },
  },
  {
    path: '/',
    name: 'Index',
    component: () => import('@/pages/home-v2/HomeV2.vue'),
    beforeEnter: middleware([authentication]),
    meta: { noPadding: true },
  },
  {
    path: '/home',
    name: 'Home',
    component: () => import('@/pages/home-v2/HomeV2.vue'),
    beforeEnter: middleware([authentication]),
    meta: { noPadding: true },
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import('@/pages/dashboard/DashboardView.vue'),
    beforeEnter: middleware([authentication, adminAuth]),
  },
  {
    path: '/dashboard/completed-modules',
    name: 'CompletedModules',
    component: () => import('@/pages/dashboard-completed-modules/DashboardCompletedModules.vue'),
    beforeEnter: middleware([authentication, adminAuth]),
  },
  {
    path: '/dashboard/completed-lessons',
    name: 'CompletedLessons',
    component: () => import('@/pages/dashboard-completed-lessons/DasboardCompletedLessons.vue'),
    beforeEnter: middleware([authentication, adminAuth]),
  },
  {
    path: '/dashboard/downloaded-files',
    name: 'DownloadedFiles',
    component: () => import('@/pages/dashboard-downloaded-files/DashboardDownloadedFiles.vue'),
    beforeEnter: middleware([authentication, adminAuth]),
  },
  {
    path: '/dashboard/rated-lessons',
    name: 'RatedLessons',
    component: () => import('@/pages/dashboard-rated-lessons/DashboardRatedLessons.vue'),
    beforeEnter: middleware([authentication, adminAuth]),
  },
  {
    path: '/my-courses/watch/course/:courseId',
    name: 'watch',
    component: () => import('@/pages/watch/WatchView.vue'),
    beforeEnter: middleware([authentication]),
    meta: { noPadding: true },
  },
  {
    path: '/my-courses/watch/course/:courseId/module/:moduleId',
    name: 'watch-module',
    component: () => import('@/pages/watch/WatchView.vue'),
    beforeEnter: middleware([authentication]),
    meta: { noPadding: true },
  },
  {
    path: '/my-courses/watch/course/:courseId/module/:moduleId/lesson/:lessonId',
    name: 'watch-lesson',
    component: () => import('@/pages/watch/WatchView.vue'),
    beforeEnter: middleware([authentication]),
    meta: { noPadding: true },
  },
  // Legacy watch paths (same component, no redirect)
  {
    path: '/watch/course/:courseId',
    component: () => import('@/pages/watch/WatchView.vue'),
    beforeEnter: middleware([authentication]),
    meta: { noPadding: true },
  },
  {
    path: '/watch/course/:courseId/module/:moduleId',
    component: () => import('@/pages/watch/WatchView.vue'),
    beforeEnter: middleware([authentication]),
    meta: { noPadding: true },
  },
  {
    path: '/watch/course/:courseId/module/:moduleId/lesson/:lessonId',
    component: () => import('@/pages/watch/WatchView.vue'),
    beforeEnter: middleware([authentication]),
    meta: { noPadding: true },
  },
  {
    path: '/new-password',
    name: 'NewPassword',
    component: () => import('@/pages/auth-new-password/AuthNewPassword.vue'),
    meta: { noLayout: true },
  },
  {
    path: '/set-password',
    name: 'SetPassword',
    component: () => import('@/pages/auth-set-password/AuthSetPassword.vue'),
    meta: { noLayout: true },
  },
  {
    path: '/my-courses',
    component: () => import('@/pages/my-courses/MyCoursesWrapper.vue'),
    beforeEnter: middleware([authentication]),
    children: [
      {
        path: '',
        name: 'MyCourses',
        component: () => import('@/pages/my-courses/MyCourses.vue'),
      },
      {
        path: 'config_curso/:courseId?',
        name: 'config-curso-v2',
        component: () => import('@/pages/course-config-v2/CourseConfigV2.vue'),
        beforeEnter: middleware([authentication, adminAuth]),
      },
    ],
  },
  {
    path: '/live-lessons',
    name: 'LiveLessons',
    component: () => import('@/pages/live-lesson/LiveLesson.vue'),
    beforeEnter: middleware([authentication]),
  },
  { path: '/live_lessons', redirect: { name: 'LiveLessons' } },
  {
    path: '/live-lessons/config',
    name: 'LiveLessonsConfig',
    component: () => import('@/pages/live-lesson-config/LiveLessonConfig.vue'),
    beforeEnter: middleware([authentication, adminAuth]),
  },
  { path: '/live_lessons_config', redirect: { name: 'LiveLessonsConfig' } },
  {
    path: '/live/:id',
    name: 'LiveLesson',
    component: () => import('@/pages/live-lesson-watch/LiveLessonWatch.vue'),
    beforeEnter: middleware([authentication]),
  },
  {
    path: '/cronograma',
    name: 'study_schedule',
    component: () => import('@/pages/stoodi-schedule/StoodiSchedule.vue'),
    beforeEnter: middleware([authentication]),
  },
  {
    path: '/cronograma/introducao',
    name: 'study_schedule_intro',
    component: () => import('@/pages/stoodi-schedule-intro/StoodiScheduleIntro.vue'),
    beforeEnter: middleware([authentication]),
  },
  {
    path: '/cronograma/criacao',
    name: 'study_schedule_creation',
    component: () => import('@/pages/stoodi-schedule-creation/StoodiScheduleCreation.vue'),
    beforeEnter: middleware([authentication]),
  },
  {
    path: '/intensivao',
    name: 'intensivao',
    component: () => import('@/pages/stoodi-intensivao/StoodiIntensivao.vue'),
    beforeEnter: middleware([authentication]),
  },
  {
    path: '/exercicios/:area',
    name: 'exercises_area',
    component: () => import('@/pages/stoodi-exercises-categories/StoodiExercisesCategories.vue'),
    beforeEnter: middleware([authentication]),
  },
  {
    path: '/exercicios/:area/:subCategory',
    name: 'exercise_list',
    component: () => import('@/pages/stoodi-exercises-list/StoodiExercisesList.vue'),
    beforeEnter: middleware([authentication]),
  },
  {
    path: '/exercicios/:area/:subCategory/:exercise',
    name: 'exercise',
    component: () => import('@/pages/stoodi-exercise/StoodiExercise.vue'),
    beforeEnter: middleware([authentication]),
  },
  {
    path: '/gamification',
    name: 'gamification',
    component: () => import('@/pages/gamification/GamificationView.vue'),
    beforeEnter: middleware([authentication, gamification]),
  },
  {
    path: '/gamification/config',
    name: 'gamification-config',
    component: () => import('@/pages/gamification-config/GamificationConfig.vue'),
    beforeEnter: middleware([authentication, gamificationConfig]),
  },
  {
    path: '/gamification/ranking',
    name: 'gamification-ranking',
    component: () => import('@/pages/gamification-ranking/GamificationRanking.vue'),
    beforeEnter: middleware([authentication, gamificationRanking]),
  },
  {
    // Links personalizados da sidebar embedados nessa rota
    path: '/link/:index',
    name: 'custom_link',
    component: () => import('@/pages/custom-link/CustomLink.vue'),
    beforeEnter: middleware([authentication]),
    meta: { noPadding: true },
  },
  {
    path: '/config_curso_v2/:courseId?',
    redirect: (to) => ({ name: 'config-curso-v2', params: to.params }),
  },
  {
    path: '/certificates',
    name: 'Certificados',
    component: () => import('@/pages/certificates/CertificatesView.vue'),
    beforeEnter: middleware([authentication]),
  },
  {
    path: '/certificate-template',
    name: 'Certificados_Template',
    component: () => import('@/pages/certificates-template/CertificatesTemplate.vue'),
    beforeEnter: middleware([authentication, adminAuth]),
  },
  {
    path: '/curso/venda/:id',
    name: 'Vender Curso',
    component: () => import('@/pages/sale-course/SaleCourse.vue'),
    beforeEnter: middleware([authentication]),
  },
  {
    path: '/comunidade',
    name: 'Comunidade',
    component: () => import('@/pages/comments/CommentsView.vue'),
    beforeEnter: middleware([authentication, adminAuth]),
  },
  {
    path: '/banners',
    name: 'Banner',
    component: () => import('@/pages/home-banner-config/HomeBannerConfig.vue'),
    beforeEnter: middleware([authentication, adminAuth]),
  },
  {
    path: '/members',
    name: 'Membros',
    component: () => import('@/pages/member/MemberView.vue'),
    beforeEnter: middleware([authentication, adminAuth]),
  },
  {
    path: '/members/:memberId',
    name: 'MemberDetail',
    component: () => import('@/pages/member-detail/MemberDetail.vue'),
    beforeEnter: middleware([authentication, adminAuth]),
  },
  {
    path: '/minha-conta',
    name: 'Minha conta',
    component: () => import('@/pages/my-account/MyAccount.vue'),
    beforeEnter: middleware([authentication]),
  },
  {
    path: '/configuracoes',
    name: 'Meu Painel',
    component: () => import('@/pages/configuration/ConfigurationView.vue'),
    beforeEnter: middleware([authentication, adminAuth]),
  },
  {
    path: '/report-problem',
    beforeEnter() {
      location.href = 'https://app.pipefy.com/public/form/0xqJRdmJ';
    },
  },
  {
    path: '/combos',
    name: 'combos',
    component: () => import('@/pages/combos/CombosView.vue'),
    beforeEnter: middleware([authentication, adminAuth]),
  },
  {
    path: '/notifications',
    name: 'Notifications',
    component: () => import('@/pages/notifications/NotificationsView.vue'),
    beforeEnter: middleware([authentication]),
    meta: { noPadding: true },
  },
  {
    path: '/redirect',
    name: 'Redirect',
    component: () => import('@/pages/redirect/RedirectView.vue'),
  },
  {
    path: '/debug',
    name: 'Debug',
    component: () => import('@/pages/debug/DebugView.vue'),
    beforeEnter: middleware([debugRoutes]),
  },
  {
    path: '/debug/modals',
    name: 'ModalShowcase',
    component: () => import('@/pages/debug/ModalShowcase.vue'),
    beforeEnter: middleware([debugRoutes]),
  },
  {
    path: '/debug/image-picker',
    name: 'ImagePickerShowcase',
    component: () => import('@/pages/debug/ImagePickerShowcase.vue'),
    beforeEnter: middleware([debugRoutes]),
  },
  {
    path: '/ds',
    name: 'DesignSystem',
    component: () => import('@/pages/ds/DsIndex.vue'),
    beforeEnter: middleware([debugRoutes]),
  },
  {
    path: '/ds/avatar-play',
    name: 'DsAvatarPlay',
    component: () => import('@/pages/ds/showcases/AvatarPlayShowcase.vue'),
    beforeEnter: middleware([debugRoutes]),
  },
  {
    path: '/ds/badge-play',
    name: 'DsBadgePlay',
    component: () => import('@/pages/ds/showcases/BadgePlayShowcase.vue'),
    beforeEnter: middleware([debugRoutes]),
  },
  {
    path: '/ds/checkbox-play',
    name: 'DsCheckboxPlay',
    component: () => import('@/pages/ds/showcases/CheckboxPlayShowcase.vue'),
    beforeEnter: middleware([debugRoutes]),
  },
  {
    path: '/ds/divider-play',
    name: 'DsDividerPlay',
    component: () => import('@/pages/ds/showcases/DividerPlayShowcase.vue'),
    beforeEnter: middleware([debugRoutes]),
  },
  {
    path: '/ds/icon-google',
    name: 'DsIconGoogle',
    component: () => import('@/pages/ds/showcases/IconGoogleShowcase.vue'),
    beforeEnter: middleware([debugRoutes]),
  },
  {
    path: '/ds/infinite-scroll-play',
    name: 'DsInfiniteScrollPlay',
    component: () => import('@/pages/ds/showcases/InfiniteScrollPlayShowcase.vue'),
    beforeEnter: middleware([debugRoutes]),
  },
  {
    path: '/ds/input-color-play',
    name: 'DsInputColorPlay',
    component: () => import('@/pages/ds/showcases/InputColorPlayShowcase.vue'),
    beforeEnter: middleware([debugRoutes]),
  },
  {
    path: '/ds/input-play-v3',
    name: 'DsInputPlayV3',
    component: () => import('@/pages/ds/showcases/InputPlayV3Showcase.vue'),
    beforeEnter: middleware([debugRoutes]),
  },
  {
    path: '/ds/input-radio-play',
    name: 'DsInputRadioPlay',
    component: () => import('@/pages/ds/showcases/InputRadioPlayShowcase.vue'),
    beforeEnter: middleware([debugRoutes]),
  },
  {
    path: '/ds/input-range-play',
    name: 'DsInputRangePlay',
    component: () => import('@/pages/ds/showcases/InputRangePlayShowcase.vue'),
    beforeEnter: middleware([debugRoutes]),
  },
  {
    path: '/ds/progress-bar-play',
    name: 'DsProgressBarPlay',
    component: () => import('@/pages/ds/showcases/ProgressBarPlayShowcase.vue'),
    beforeEnter: middleware([debugRoutes]),
  },
  {
    path: '/ds/textarea-play-v2',
    name: 'DsTextareaPlayV2',
    component: () => import('@/pages/ds/showcases/TextareaPlayV2Showcase.vue'),
    beforeEnter: middleware([debugRoutes]),
  },
  {
    path: '/ds/text-play',
    name: 'DsTextPlay',
    component: () => import('@/pages/ds/showcases/TextPlayShowcase.vue'),
    beforeEnter: middleware([debugRoutes]),
  },
  {
    path: '/ds/toggle-button-play',
    name: 'DsToggleButtonPlay',
    component: () => import('@/pages/ds/showcases/ToggleButtonPlayShowcase.vue'),
    beforeEnter: middleware([debugRoutes]),
  },
  {
    path: '/ds/button-play-v2',
    name: 'DsButtonPlayV2',
    component: () => import('@/pages/ds/showcases/ButtonPlayV2Showcase.vue'),
    beforeEnter: middleware([debugRoutes]),
  },
  {
    path: '/ds/date-picker-play',
    name: 'DsDatePickerPlay',
    component: () => import('@/pages/ds/showcases/DatePickerPlayShowcase.vue'),
    beforeEnter: middleware([debugRoutes]),
  },
  {
    path: '/ds/dropdown-play',
    name: 'DsDropdownPlay',
    component: () => import('@/pages/ds/showcases/DropdownPlayShowcase.vue'),
    beforeEnter: middleware([debugRoutes]),
  },
  {
    path: '/ds/field-play',
    name: 'DsFieldPlay',
    component: () => import('@/pages/ds/showcases/FieldPlayShowcase.vue'),
    beforeEnter: middleware([debugRoutes]),
  },
  {
    path: '/ds/form-field-play',
    name: 'DsFormFieldPlay',
    component: () => import('@/pages/ds/showcases/FormFieldPlayShowcase.vue'),
    beforeEnter: middleware([debugRoutes]),
  },
  {
    path: '/ds/image-picker-play',
    name: 'DsImagePickerPlay',
    component: () => import('@/pages/ds/showcases/ImagePickerPlayShowcase.vue'),
    beforeEnter: middleware([debugRoutes]),
  },
  {
    path: '/ds/input-date-time-play',
    name: 'DsInputDateTimePlay',
    component: () => import('@/pages/ds/showcases/InputDateTimePlayShowcase.vue'),
    beforeEnter: middleware([debugRoutes]),
  },
  {
    path: '/ds/modal-play',
    name: 'DsModalPlay',
    component: () => import('@/pages/ds/showcases/ModalPlayShowcase.vue'),
    beforeEnter: middleware([debugRoutes]),
  },
  {
    path: '/ds/multiselect-play-v2',
    name: 'DsMultiselectPlayV2',
    component: () => import('@/pages/ds/showcases/MultiselectPlayV2Showcase.vue'),
    beforeEnter: middleware([debugRoutes]),
  },
  {
    path: '/ds/pagination-play',
    name: 'DsPaginationPlay',
    component: () => import('@/pages/ds/showcases/PaginationPlayShowcase.vue'),
    beforeEnter: middleware([debugRoutes]),
  },
  {
    path: '/ds/select-play-v2',
    name: 'DsSelectPlayV2',
    component: () => import('@/pages/ds/showcases/SelectPlayV2Showcase.vue'),
    beforeEnter: middleware([debugRoutes]),
  },
  {
    path: '/ds/star-rating-play',
    name: 'DsStarRatingPlay',
    component: () => import('@/pages/ds/showcases/StarRatingPlayShowcase.vue'),
    beforeEnter: middleware([debugRoutes]),
  },
  {
    path: '/ds/tabs-play-v2',
    name: 'DsTabsPlayV2',
    component: () => import('@/pages/ds/showcases/TabsPlayV2Showcase.vue'),
    beforeEnter: middleware([debugRoutes]),
  },
  {
    path: '/ds/tel-input-play',
    name: 'DsTelInputPlay',
    component: () => import('@/pages/ds/showcases/TelInputPlayShowcase.vue'),
    beforeEnter: middleware([debugRoutes]),
  },
  {
    path: '/ds/text-editor-play',
    name: 'DsTextEditorPlay',
    component: () => import('@/pages/ds/showcases/TextEditorPlayShowcase.vue'),
    beforeEnter: middleware([debugRoutes]),
  },
  {
    path: '/ds/toast-play',
    name: 'DsToastPlay',
    component: () => import('@/pages/ds/showcases/ToastPlayShowcase.vue'),
    beforeEnter: middleware([debugRoutes]),
  },
  {
    path: '/ds/breadcrumb-play',
    name: 'DsBreadcrumbPlay',
    component: () => import('@/pages/ds/showcases/BreadcrumbPlayShowcase.vue'),
    beforeEnter: middleware([debugRoutes]),
  },
  {
    path: '/api-docs',
    name: 'API Docs',
    component: () => import('@/pages/api-docs/API-Docs.vue'),
    beforeEnter: middleware([authentication]),
    meta: { noLayout: true },
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/home',
  },
];

export default routes;
